import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FormEvent, useEffect, useState } from 'react';
import useMyAccountQuery from '#hooks/queries/accounts/useMyAccountQuery';
import useDeleteNoticeMutation from '#hooks/queries/gymAdmin/useDeleteNoticeMutation';
import usePatchNoticeMutation from '#hooks/queries/gymAdmin/usePatchNoticeMutation';
import usePostNoticeMutation from '#hooks/queries/gymAdmin/usePostNoticeMutation';
import queryKeys from '#hooks/queries/queryKeys';
import useCoachMemoQuery from '#hooks/queries/schedules/useCoachMemoQuery';
import useCoachNoticeQuery from '#hooks/queries/schedules/useCoachNoticeQuery';
import useDeleteMemoMutation from '#hooks/queries/schedules/useDeleteMemoMutation';
import usePatchCoachMemoMutation from '#hooks/queries/schedules/usePatchMemoMutation';
import usePostCoachMemoMutation from '#hooks/queries/schedules/usePostCoachMemoMutation';
import { useModalConfirm } from '#hooks/useModalConfirm';
import { ScheduleMemoType } from '#types/schedule';
import Icon from 'src/components/atoms/Icon';
import Portal from 'src/components/shared/Portal';
import { CalendarPeriodic } from '../../types';
import { KEY_HIDDEN_NOTICE } from '../CardMemoWrapper/CardNotice';
import * as S from './styles';

type TabType = 'NOTICE' | ScheduleMemoType;

interface Props {
  open: boolean;
  activeDate: dayjs.Dayjs;
  calendarPeriodic: CalendarPeriodic;
  handleClose: () => void;
  onSubmit: (memo: string, activeTab: TabType) => void;
}

interface ButtonGroupProps extends Pick<Props, 'handleClose'> {
  activeTab?: TabType;
  isGymAdmin: boolean;
  isEditMode?: boolean;
}

interface TabListItem {
  id: TabType;
  text: string;
}

const tabListTemplate: TabListItem[] = [
  {
    id: 'NOTICE',
    text: '공지'
  }
];

export default function ModalEditMemo({
  open,
  activeDate,
  handleClose,
  calendarPeriodic,
  onSubmit
}: Props) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>(calendarPeriodic);
  const [tabList, setTabList] = useState<TabListItem[]>(tabListTemplate);
  const [memoValue, setMemoValue] = useState<string>();
  const { openModalConfirm } = useModalConfirm();
  const { data: account } = useMyAccountQuery();

  const { data: notice } = useCoachNoticeQuery();
  const { data: memo } = useCoachMemoQuery({
    memoType: activeTab === 'NOTICE' ? undefined : activeTab,
    date: activeDate?.format('YYYY-MM-DD'),
    year: activeDate?.get('year'),
    month: activeDate?.get('month') ? activeDate?.get('month') + 1 : undefined,
    week: activeDate?.week()
  });
  const { mutateAsync: postMemo } = usePostCoachMemoMutation();
  const { mutateAsync: patchMemo } = usePatchCoachMemoMutation();
  const { mutateAsync: deleteMemo } = useDeleteMemoMutation();
  const { mutateAsync: postNotice } = usePostNoticeMutation();
  const { mutateAsync: patchNotice } = usePatchNoticeMutation();
  const { mutateAsync: deleteNotice } = useDeleteNoticeMutation();
  const [isMemoEditMode, setIsMemoEditMode] = useState<boolean>();
  const [isNoticeEditMode, setIsNoticeEditMode] = useState<boolean>();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (activeTab === 'NOTICE') {
      handleSubnmitNotice();
    } else {
      handleSubmitMemo();
    }
  };

  const handleSubmitMemo = async () => {
    const payload: Record<string, number | string | undefined> = {
      memoType: activeTab,
      memo: memoValue,
      date: activeDate?.format('YYYY-MM-DD')
    };

    if (activeTab === 'WEEKLY') {
      payload['year'] = activeDate?.get('year');
      payload['week'] = activeDate?.week();
    }

    if (!isMemoEditMode) {
      // @ts-ignore
      await postMemo(payload);
      queryClient.invalidateQueries([queryKeys.SCHEDULE, 'memo']);
      handleClose();
    } else {
      if (!memoValue) {
        openModalConfirm({
          contents: '메모를 삭제 하시겠습니까?',
          confirmText: '삭제',
          async onClickConfirm() {
            await deleteMemo({ coachMemoId: memo?.id as string });
            queryClient.invalidateQueries([queryKeys.SCHEDULE, 'memo']);
            handleClose();
          }
        });
      } else {
        // @ts-ignore
        await patchMemo({
          coachMemoId: memo?.id as string,
          memo: memoValue as string
        });
        queryClient.invalidateQueries([queryKeys.SCHEDULE, 'memo']);
        handleClose();
      }
    }
  };

  const handleSubnmitNotice = async () => {
    const payload: Record<string, number | string | undefined> = {
      title: memoValue,
      body: memoValue
    };

    if (!isNoticeEditMode) {
      // @ts-ignore
      await postNotice(payload);
      queryClient.invalidateQueries([queryKeys.SCHEDULE, 'notice']);
      handleClose();
    } else {
      if (!memoValue) {
        await openModalConfirm({
          contents: '공지를 삭제 하시겠습니까?',
          confirmText: '삭제',
          async onClickConfirm() {
            await deleteNotice({ gymNoticeId: notice?.id as string });
            queryClient.invalidateQueries([queryKeys.SCHEDULE, 'notice']);
            window.sessionStorage.removeItem(KEY_HIDDEN_NOTICE);
            handleClose();
          }
        });
      } else {
        // @ts-ignore
        await patchNotice({
          gymNoticeId: notice?.id as string,
          ...payload
        });
        queryClient.invalidateQueries([queryKeys.SCHEDULE, 'notice']);
        window.sessionStorage.removeItem(KEY_HIDDEN_NOTICE);
        handleClose();
      }
    }

    queryClient.invalidateQueries([queryKeys.SCHEDULE, 'notice']);
  };

  const getPlaceholder = () => {
    switch (activeTab) {
      case 'NOTICE':
        return account?.isGymAdmin
          ? '공지 내용을 입력해주세요.'
          : '등록된 공지가 없습니다.';
      case 'DAILY':
        return '일간 메모 내용을 입력해주세요.';
      case 'WEEKLY':
        return '주간 메모 내용을 입력해주세요.';
      default:
        return '메모 내용을 입력해주세요.';
    }
  };

  useEffect(() => {
    setTabList([
      {
        id: calendarPeriodic,
        text: calendarPeriodic === 'WEEKLY' ? '주간' : '일간'
      },
      ...(notice?.isVisible && notice?.gymHasAdminCoach ? tabListTemplate : [])
    ]);
  }, [calendarPeriodic, notice]);

  useEffect(() => {
    if (activeTab !== 'NOTICE') {
      if (memo?.id && activeTab === memo?.memoType) {
        setMemoValue(memo.memo);
        setIsMemoEditMode(true);
      } else {
        setMemoValue('');
        setIsMemoEditMode(false);
      }
    }
  }, [memo, activeTab]);

  useEffect(() => {
    if (activeTab === 'NOTICE') {
      if (notice?.id) {
        setMemoValue(notice.body);
        setIsNoticeEditMode(true);
      } else {
        setMemoValue('');
        setIsNoticeEditMode(false);
      }
    }
  }, [notice, activeTab]);

  return (
    <Portal portalId="memo-modal">
      <S.Root>
        <S.Bg />
        <S.Wrapper>
          <form onSubmit={handleSubmit}>
            <S.HeaderWrapper>
              <S.Title>메모</S.Title>
              <S.ButtonClose type="button" onClick={handleClose}>
                <Icon name="IconClose" />
              </S.ButtonClose>
            </S.HeaderWrapper>
            {tabList.length > 0 && (
              <S.TabListWrapper>
                {tabList.map(item => (
                  <S.TabItem
                    type="button"
                    isActive={activeTab === item.id}
                    onClick={() => setActiveTab(item.id)}
                  >
                    {item.text}
                  </S.TabItem>
                ))}
              </S.TabListWrapper>
            )}
            <S.TextareaWrapper>
              <S.Textarea
                value={memoValue}
                onChange={e => setMemoValue(e.target.value)}
                placeholder={getPlaceholder()}
                readOnly={!account?.isGymAdmin && activeTab === 'NOTICE'}
              ></S.Textarea>
            </S.TextareaWrapper>

            <ButtonGroup
              isGymAdmin={!!account?.isGymAdmin}
              activeTab={activeTab}
              isEditMode={isMemoEditMode}
              handleClose={handleClose}
            />
          </form>
        </S.Wrapper>
      </S.Root>
    </Portal>
  );
}

function ButtonGroup({
  isGymAdmin,
  activeTab,
  isEditMode,
  handleClose
}: ButtonGroupProps) {
  if (activeTab === 'NOTICE' && !isGymAdmin) {
    return (
      <S.ButtonGroup>
        <S.Button type="button" onClick={handleClose}>
          닫기
        </S.Button>
      </S.ButtonGroup>
    );
  }

  return (
    <S.ButtonGroup>
      <S.Button type="button" onClick={handleClose}>
        닫기
      </S.Button>
      <S.ButtonPrimary type="submit">
        {isEditMode ? '수정' : '저장'}
      </S.ButtonPrimary>
    </S.ButtonGroup>
  );
}
